<template>
  <section
    class="br-admin-notes"
    :class="{ 'br-admin-notes--empty': !loading && !notes.length }"
  >
    <header class="br-admin-notes__header">
      <h3 class="br-admin-notes__title">{{ $t('lbl_EMPBE_BR_AdminNote_Title') }}</h3>
    </header>

    <BrAdminNotesSkeleton
      v-if="loading"
      :count="2"
      role="status"
      aria-live="polite"
      :aria-label="$t('lbl_EMPBE_BR_AdminNote_Loading')"
    />

    <BrAdminNotesList
      v-else-if="notes.length"
      :notes="notes"
      @preview="onPreviewAttachment"
    />

    <BrAdminNotesSkeleton
      v-if="submitting && !loading"
      aria-hidden="true"
    />

    <BrAdminNotesComposer
      :draft-remark.sync="draftRemark"
      :pending-files="pendingFiles"
      :submitting="submitting"
      :loading="loading"
      :emp-company-id="empCompanyId"
      :can-save="canSave"
      :save-button-label="saveButtonLabel"
      :error-text.sync="errorText"
      :accept-attr="acceptAttr"
      :max-attachments="maxAttachments"
      :file-rule-params="fileRuleParams"
      :current-user-initial="currentUserInitial"
      @accept-files="onAcceptFiles"
      @remove-pending="removePending"
      @submit="onSubmitRemark"
    />

    <!-- 原型：当前页 Document Preview；图片支持悬停放大（与 BR 预览一致） -->
    <PhotoPreview
      v-model="showAttachmentPreview"
      :title="$t('lbl_EMPBE_BR_DocumentPreview')"
    >
      <template #content>
        <div
          class="br-admin-notes__preview"
          :class="{ 'br-admin-notes__preview--pdf': previewIsPdf }"
        >
          <iframe
            v-if="previewIsPdf"
            class="br-admin-notes__preview-frame"
            :src="previewSrc"
            title="Document Preview"
          />
          <BrImageSideZoom
            v-else-if="previewSrc"
            :src="previewSrc"
            :enabled="showAttachmentPreview"
            img-class="br-admin-notes__preview-img"
          />
        </div>
      </template>
    </PhotoPreview>
  </section>
</template>

<script>
import PhotoPreview from '@/components/PhotoPreview/PhotoPreview.vue';
import BrImageSideZoom from '@/pages/businessDocuments/components/BrImageSideZoom.vue';
import BrAdminNotesList from './BrAdminNotesList.vue';
import BrAdminNotesComposer from './BrAdminNotesComposer.vue';
import BrAdminNotesSkeleton from './BrAdminNotesSkeleton.vue';
import { BusinessRegistrationService } from '@/_services';
import { fileUploadService } from '@/_services/fileUpload.service';
import { resolveApiErrorMessage } from '@/_helpers/apiResponse';
import {
  BR_ADMIN_NOTE_ACCEPT_ATTR,
  BR_ADMIN_NOTE_ACCEPT_LABEL,
  BR_ADMIN_NOTE_MAX_ATTACHMENTS,
  BR_ADMIN_NOTE_MAX_FILE_SIZE_MB,
} from '@/constants/brAdminNote';

export default {
  name: 'BrAdminNotesPanel',
  components: {
    PhotoPreview,
    BrImageSideZoom,
    BrAdminNotesList,
    BrAdminNotesComposer,
    BrAdminNotesSkeleton,
  },
  props: {
    empCompanyId: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      notes: [],
      pendingFiles: [],
      draftRemark: '',
      errorText: '',
      maxAttachments: BR_ADMIN_NOTE_MAX_ATTACHMENTS,
      maxSizeMb: BR_ADMIN_NOTE_MAX_FILE_SIZE_MB,
      acceptAttr: BR_ADMIN_NOTE_ACCEPT_ATTR,
      acceptFormatsLabel: BR_ADMIN_NOTE_ACCEPT_LABEL,
      showAttachmentPreview: false,
      previewSrc: '',
      previewIsPdf: false,
    };
  },
  computed: {
    companyIdNum() {
      const n = Number(this.empCompanyId);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    currentUserDisplayName() {
      const u = this.$store?.state?.authentication?.user || {};
      return String(
        u.personName
        || u.personNameEn
        || u.personNameSc
        || u.displayName
        || u.userName
        || u.loginUser
        || '',
      ).trim();
    },
    currentUserInitial() {
      const name = this.currentUserDisplayName;
      return name ? name.charAt(0).toUpperCase() : 'A';
    },
    canSave() {
      return !!this.companyIdNum
        && !!String(this.draftRemark || '').trim()
        && !this.loading;
    },
    saveButtonLabel() {
      // Figma busy：spinner + 文案仍为「保存」
      return this.$t('lbl_EMPBE_BR_AdminNote_Post');
    },
    fileRuleParams() {
      return {
        max: this.maxAttachments,
        size: this.maxSizeMb,
        formats: this.acceptFormatsLabel,
      };
    },
    isBusy() {
      return !!this.submitting;
    },
  },
  watch: {
    companyIdNum: {
      immediate: true,
      handler(id) {
        if (id > 0) {
          this.loadNotes();
        } else {
          this.notes = [];
        }
      },
    },
    isBusy(busy) {
      this.$emit('busy-change', busy);
      this.syncLeaveGuards(busy);
    },
  },
  created() {
    this.installRouterGuard();
  },
  beforeDestroy() {
    this.clearPendingFiles();
    this.syncLeaveGuards(false);
    this.removeRouterGuard();
  },
  methods: {
    installRouterGuard() {
      if (!this.$router || this._removeRouterGuard) return;
      this._removeRouterGuard = this.$router.beforeEach((to, from, next) => {
        if (!this.isBusy) {
          next();
          return;
        }
        // eslint-disable-next-line no-alert
        window.alert(this.$t('lbl_EMPBE_BR_AdminNote_BusyLeave'));
        next(false);
      });
    },
    removeRouterGuard() {
      if (typeof this._removeRouterGuard === 'function') {
        this._removeRouterGuard();
        this._removeRouterGuard = null;
      }
    },
    syncLeaveGuards(busy) {
      if (busy) {
        if (!this._onBeforeUnload) {
          this._onBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = '';
          };
          window.addEventListener('beforeunload', this._onBeforeUnload);
        }
        return;
      }
      if (this._onBeforeUnload) {
        window.removeEventListener('beforeunload', this._onBeforeUnload);
        this._onBeforeUnload = null;
      }
    },
    onAcceptFiles(accepted) {
      this.pendingFiles = this.pendingFiles.concat(accepted).slice(0, this.maxAttachments);
    },
    removePending(index) {
      const removed = this.pendingFiles[index];
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      this.pendingFiles.splice(index, 1);
    },
    clearPendingFiles() {
      this.pendingFiles.forEach((file) => {
        if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
      });
      this.pendingFiles = [];
    },
    fileDisplayNameFromUrl(url) {
      const raw = String(url || '');
      if (!raw) return 'file';
      const noQuery = raw.split('?')[0];
      const part = noQuery.includes('/') ? noQuery.split('/').pop() : noQuery;
      return part || 'file';
    },
    async loadNotes(options = {}) {
      if (!this.companyIdNum) return;
      const silent = !!options.silent;
      if (!silent) {
        this.loading = true;
      }
      this.errorText = '';
      try {
        const res = await BusinessRegistrationService.ListAdminNotes(this.companyIdNum);
        if (!res?.isSuccessful) {
          throw new Error(resolveApiErrorMessage(res) || this.$t('lbl_EMPBE_BR_AdminNote_LoadFailed'));
        }
        const raw = Array.isArray(res.data) ? res.data : [];
        // 对齐 Sales Lead MessageThread：CreateTime 升序（由远至近，旧上新下）
        this.notes = raw
          .map((row) => this.normalizeNote(row))
          .sort((a, b) => {
            const ta = new Date(a.createTime).getTime();
            const tb = new Date(b.createTime).getTime();
            const na = Number.isFinite(ta) ? ta : 0;
            const nb = Number.isFinite(tb) ? tb : 0;
            if (na !== nb) return na - nb;
            return (a.adminNoteId || 0) - (b.adminNoteId || 0);
          });
      } catch (err) {
        if (!silent) {
          this.notes = [];
        }
        this.errorText = err?.message || this.$t('lbl_EMPBE_BR_AdminNote_LoadFailed');
      } finally {
        if (!silent) {
          this.loading = false;
        }
      }
    },
    normalizeNote(row) {
      const attachments = Array.isArray(row?.attachments || row?.Attachments)
        ? (row.attachments || row.Attachments)
        : [];
      return {
        adminNoteId: Number(row.adminNoteId ?? row.AdminNoteId),
        empCompanyId: Number(row.empCompanyId ?? row.EmpCompanyId),
        remark: String(row.remark ?? row.Remark ?? ''),
        accountUserId: Number(row.accountUserId ?? row.AccountUserId),
        createUser: String(row.createUser ?? row.CreateUser ?? ''),
        createTime: row.createTime ?? row.CreateTime,
        attachments: attachments.map((a) => {
          const fileUrl = String(a.fileUrl ?? a.FileUrl ?? a.fileName ?? a.FileName ?? '');
          const fileName = String(a.fileName ?? a.FileName ?? '');
          const original = String(a.originalFileName ?? a.OriginalFileName ?? '').trim();
          const displayName = original
            || this.fileDisplayNameFromUrl(fileName)
            || this.fileDisplayNameFromUrl(fileUrl)
            || 'file';
          return {
            attachmentId: Number(a.attachmentId ?? a.AttachmentId),
            fileName,
            originalFileName: displayName,
            fileUrl: fileUrl || fileName,
            contentType: String(a.contentType ?? a.ContentType ?? ''),
            fileSizeBytes: a.fileSizeBytes ?? a.FileSizeBytes ?? null,
            sortOrder: Number(a.sortOrder ?? a.SortOrder ?? 0),
          };
        }),
      };
    },
    async onSubmitRemark() {
      const remark = String(this.draftRemark || '').trim();
      if (!remark || !this.companyIdNum || this.submitting) return;
      this.submitting = true;
      this.errorText = '';
      try {
        const uploaded = [];
        for (let i = 0; i < this.pendingFiles.length; i += 1) {
          const pending = this.pendingFiles[i];
          const file = pending.raw || pending;
          const uploadRes = await fileUploadService.uploadFile(file, 'File', this.maxSizeMb);
          const cdn = uploadRes?.data?.cdnLnk || uploadRes?.data?.CdnLnk || '';
          if (!cdn) {
            throw new Error(this.$t('lbl_EMPBE_BR_AdminNote_UploadFailed'));
          }
          uploaded.push({
            fileName: String(cdn).split('/').pop() || pending.name || file.name,
            originalFileName: pending.name || file.name,
            fileUrl: cdn,
            sortOrder: i,
          });
        }

        const res = await BusinessRegistrationService.CreateAdminNote({
          empCompanyId: this.companyIdNum,
          remark,
          createUser: this.currentUserDisplayName,
          attachments: uploaded,
        });
        if (!res?.isSuccessful) {
          throw new Error(resolveApiErrorMessage(res) || this.$t('lbl_EMPBE_BR_AdminNote_CreateFailed'));
        }
        this.draftRemark = '';
        this.clearPendingFiles();
        await this.loadNotes({ silent: true });
      } catch (err) {
        this.errorText = err?.message || this.$t('lbl_EMPBE_BR_AdminNote_CreateFailed');
      } finally {
        this.submitting = false;
      }
    },
    isImagePath(nameOrUrl) {
      return /\.(png|jpe?g|gif|bmp|svg|webp)($|\?)/i.test(String(nameOrUrl || ''));
    },
    isPdfPath(nameOrUrl) {
      return /\.pdf($|\?)/i.test(String(nameOrUrl || ''));
    },
    onPreviewAttachment(file) {
      const url = String(file?.fileUrl || '').trim();
      if (!url) return;
      const nameHint = file?.originalFileName || file?.fileName || url;
      const isPdf = this.isPdfPath(nameHint) || this.isPdfPath(url);
      const isImage = this.isImagePath(nameHint) || this.isImagePath(url)
        || String(file?.contentType || '').startsWith('image/');
      // 图片 / PDF：当前页弹窗；其它类型仍新开标签
      if (isPdf || isImage) {
        this.previewIsPdf = isPdf;
        this.previewSrc = url;
        this.showAttachmentPreview = true;
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style scoped lang="scss">
@import '../styles/_br-admin-notes-panel.scss';
</style>
